import { NgClass, NgTemplateOutlet } from '@angular/common';
import {
  AfterViewInit,
  Component,
  computed,
  effect,
  ElementRef,
  HostBinding,
  OnInit,
  signal,
  viewChild,
} from '@angular/core';
import { FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, ParamMap, Router } from '@angular/router';
import { AbEntrySearchService } from '@maximizer/core/global-search/data-access';
import {
  SessionStorageService,
  FeatureService,
  GlobalStore,
  AiUserDataGroup,
} from '@maximizer/core/shared/data-access';
import { ListItem, Octopus } from '@maximizer/core/shared/domain';
import { InsightsService } from '@maximizer/core/shared/insights';
import { UI_LOADER } from '@maximizer/core/shared/ui/components/loader';
import {
  FormGroupWrapper,
  FormWrapperBuilder,
} from '@maximizer/core/shared/ui/forms';
import { UI_KENDO } from '@maximizer/core/shared/ui/kendo';
import { StepGuideComponent } from '@maximizer/outlook/onboarding/ui';
import {
  AbEntryService,
  NavigationService,
  OnboardingService,
  OnboardingStepNumber,
  OnboardingStore,
  OutlookService,
  OutlookStore,
  StepGuideId,
  TrackerService,
  EmailSignatureAiService,
  OutlookInsightsEvents,
  EmailSignatureFields,
  AI_EXTRACTION_MIN_BODY_LENGTH,
} from '@maximizer/outlook/shared/data-access';
import {
  EntryType,
  EventAction,
  EventJourney,
  EventMetadata,
  EventModule,
  OutlookAbEntryDetails,
  OutlookAbEntryForm,
} from '@maximizer/outlook/shared/domain';
import {
  ONBOARDING_STEP_POPOVER_OPEN_DELAY,
  ONBOARDING_STEP_POPOVER_WIDTH,
  OUTLOOK_SHARED_UI,
  OutlookNotificationComponent,
  PHONE_VALIDATORS,
} from '@maximizer/outlook/shared/ui';
import { TranslatePipe } from '@ngx-translate/core';
import { KENDO_BUTTON } from '@progress/kendo-angular-buttons';
import {
  AutoCompleteComponent,
  KENDO_DROPDOWNS,
} from '@progress/kendo-angular-dropdowns';
import { KENDO_INPUTS } from '@progress/kendo-angular-inputs';
import { KENDO_LABEL } from '@progress/kendo-angular-label';
import {
  KENDO_TOOLTIPS,
  PopoverContainerDirective,
} from '@progress/kendo-angular-tooltip';
import {
  catchError,
  finalize,
  forkJoin,
  Observable,
  firstValueFrom,
  of,
  switchMap,
  tap,
} from 'rxjs';
import { SeverityLevel } from '@microsoft/applicationinsights-web';
import { AiFeaturesService } from '@maximizer/ai/data-access';

@Component({
  selector: 'maximizer-add-abentry',
  templateUrl: './add-abentry.component.html',
  imports: [
    FormsModule,
    KENDO_BUTTON,
    KENDO_DROPDOWNS,
    KENDO_INPUTS,
    KENDO_LABEL,
    KENDO_TOOLTIPS,
    NgClass,
    NgTemplateOutlet,
    OUTLOOK_SHARED_UI,
    ReactiveFormsModule,
    StepGuideComponent,
    TranslatePipe,
    UI_LOADER,
    UI_KENDO,
  ],
})
export class AddAbEntryComponent implements OnInit, AfterViewInit {
  @HostBinding('class.max-outlook') hostClass = true;

  pageContainer =
    viewChild.required<ElementRef<HTMLDivElement>>('pageContainer');
  notification =
    viewChild.required<OutlookNotificationComponent>('notification');
  autoComplete = viewChild.required<AutoCompleteComponent>('autoComplete');
  addContact2Container = viewChild<PopoverContainerDirective>(
    'addContact2Container',
  );
  addContact2Anchor = viewChild<Element>('addContact2Anchor');

  private readonly newPartitionKey = 'new-item';
  private readonly addAbEntryPath = '/abentry/add/';

  readonly stepPopoverWidth = ONBOARDING_STEP_POPOVER_WIDTH;

  type = signal<EntryType>(EntryType.Contact);

  EntryType = EntryType;
  loading = false;
  aiLoading = false;
  abEntryList?: ListItem<string>[];
  filteredAbEntryList: ListItem<string>[] = [];
  abentry: OutlookAbEntryDetails | null = null;
  phones: { type: string; value: string }[] = [];
  emails: { type: string; value: string }[] = [];
  emailOptions: string[] = [];
  phoneOptions: string[] = [];
  countries: ListItem<string>[] = [];
  phoneLimit = 4;
  emailLimit = 3;
  autocompleteLoading = false;
  createCompanyLoading = false;
  previousCompanyAutocompleteInput = '';
  companyNameSearch = '';
  companySelected: ListItem<string> | null = null;
  companyValidatorSet = false;
  StepGuideId = StepGuideId;
  isFromContactToIndividualFlow = false;
  navigatedFromContactPage = false;
  form?: FormGroupWrapper<OutlookAbEntryForm>;
  originalContactFormState?: FormGroupWrapper<OutlookAbEntryForm>;

  createdIndividual: ListItem<string> = {
    id: '',
    name: '',
  };

  onboardingGuide = computed(() => this.onboardingStore.inProgress());

  // AI feature flags and settings
  aiEnabled = false;
  aiAdminOutlookEnabled = false;
  aiUserOutlookEnabled = false;
  aiOutlookFeatureFlagEnabled = false;
  aiPrefillFeatureFlagEnabled = false;

  aiPrefillSettingsEnabled = computed(
    () =>
      this.aiEnabled &&
      this.aiAdminOutlookEnabled &&
      this.aiUserOutlookEnabled &&
      this.aiOutlookFeatureFlagEnabled &&
      this.aiPrefillFeatureFlagEnabled,
  );

  constructor(
    private readonly abentryService: AbEntryService,
    private readonly abEntrySearchService: AbEntrySearchService,
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly storage: SessionStorageService,
    private readonly outlookService: OutlookService,
    private readonly navigationService: NavigationService,
    private readonly tracker: TrackerService,
    private readonly signatureAi: EmailSignatureAiService,
    private readonly insightsService: InsightsService,
    private readonly feature: FeatureService,
    private readonly aiFeaturesService: AiFeaturesService,
    public onboardingService: OnboardingService,
    public onboardingStore: OnboardingStore,
    public outlookStore: OutlookStore,
    public globalStore: GlobalStore,
  ) {
    effect(() => {
      const inProgress = this.onboardingGuide();
      const container = this.addContact2Container();
      const anchor = this.addContact2Anchor();

      if (inProgress && container && anchor) {
        container.show(anchor);
      } else if (!inProgress) {
        container?.hide();
      }
    });

    this.aiOutlookFeatureFlagEnabled = this.feature.isFeatureOn(
      'integration-o365-ai-features-enabled',
      false,
    );

    this.aiPrefillFeatureFlagEnabled = this.feature.isFeatureOn(
      'integration-o365-ai-prefill-entry-shown',
      false,
    );
  }

  ngOnInit(): void {
    this.route.paramMap.subscribe((params) => {
      // Update component if the entry type changes (ex: returning from add individual)
      this.handleRouteTypeChange(params);
    });
  }

  ngAfterViewInit(): void {
    if (this.onboardingStore.inProgress()) {
      this.showOnboardingGuide();
    }
  }

  private handleRouteTypeChange(params: ParamMap): void {
    const type = params.get('type') as EntryType | null;

    if (!type) {
      this.router.navigate(['/']);
      return;
    }

    this.type.set(type);

    this.loadData();
    this.scrollToTop();
  }

  private scrollToTop(): void {
    setTimeout(() => {
      this.pageContainer().nativeElement.scrollTo({
        top: 0,
        behavior: 'smooth',
      });
    });
  }

  private loadData(): void {
    this.loading = true;

    const requests = {
      fieldOptions: this.abentryService.getFieldOption(),
      aiEnabled: this.aiFeaturesService.getEnabled(),
    };

    forkJoin(requests)
      .pipe(
        tap((result) => {
          this.aiEnabled = result?.aiEnabled ?? false;
          if (result?.fieldOptions) {
            this.phoneOptions = result.fieldOptions.phoneDescriptions.map(
              (phone) => phone.id,
            );
            this.emailOptions = result.fieldOptions.emailDescriptions.map(
              (email) => email.id,
            );
            this.countries = result.fieldOptions.addressCountry;
          }
        }),
        switchMap((result) =>
          result?.aiEnabled ? this.getAIFeatureSettings() : of(null),
        ),
        catchError(() => {
          this.notification().show('load', 'error');
          return of(null);
        }),
        finalize(() => (this.loading = false)),
      )
      .subscribe(() => {
        this.setForm();
      });
  }

  private getAIFeatureSettings(): Observable<AiUserDataGroup | null> {
    const session = this.globalStore.session();
    if (!session) {
      return of(null);
    }

    return this.aiFeaturesService.getAIUserData(session).pipe(
      tap((result) => {
        if (result) {
          this.aiAdminOutlookEnabled = result.admin.aiFeaturesEnabled.outlook;
          this.aiUserOutlookEnabled = result.user.aiFeaturesEnabled.outlook;
        }
      }),
      catchError(() => of(null)),
    );
  }

  private setForm(): void {
    try {
      const queryParams = this.route.snapshot.queryParamMap;
      const email = queryParams.get('email') ?? '';
      const name = queryParams.get('name') ?? '';
      let firstName = '';
      let lastName = '';

      if (name) {
        const fullName = name.split(' ');
        if (fullName.length === 1) {
          lastName = fullName[0];
        } else {
          firstName = fullName[0] ?? '';
          lastName = fullName[1] ?? '';
        }
      }

      const isAddressDisabled = this.type() === EntryType.Contact;
      const initContactFormState =
        !this.navigatedFromContactPage && !this.isFromContactToIndividualFlow;

      if (initContactFormState) {
        this.emails.push({ type: '', value: email });
        this.phones.push({ type: '', value: '' });

        this.originalContactFormState = this.buildContactForm(
          firstName,
          lastName,
          isAddressDisabled,
          this.type(),
          this.emails,
          this.navigatedFromContactPage,
          this.companyNameSearch,
        );
      }

      if (
        this.isFromContactToIndividualFlow &&
        this.originalContactFormState &&
        this.type() === EntryType.Contact
      ) {
        const saved = this.originalContactFormState.value;

        this.form = this.buildContactForm(
          saved.firstName,
          saved.lastName,
          isAddressDisabled,
          this.type(),
          this.emails,
          this.navigatedFromContactPage,
          this.companyNameSearch,
        );

        this.form.patch({
          ...saved,
          parentKey: this.createdIndividual.id,
          company: this.createdIndividual.name,
        });

        this.loadCompanyAddress();
      } else {
        this.form = this.buildContactForm(
          firstName,
          lastName,
          isAddressDisabled,
          this.type(),
          this.emails,
          this.navigatedFromContactPage,
          this.companyNameSearch,
        );
      }

      this.form?.control.setErrors(null);

      if (this.aiPrefillSettingsEnabled()) {
        this.initializeAiPrefill();
      }
    } catch (error) {
      console.error(error);
    }
  }

  save(): void {
    if (!this.form) return;

    const validation = this.form.validate();
    if (!validation.valid) return;

    this.loading = true;

    this.abentryService
      .save(this.form.value)
      .pipe(
        catchError(() => {
          this.notification().show('create', 'error');
          return of('');
        }),
        finalize(() => {
          this.loading = false;

          if (
            this.onboardingStore.inProgress() &&
            !this.navigatedFromContactPage &&
            !this.isFromContactToIndividualFlow
          ) {
            this.nextOnboardingStep(OnboardingStepNumber.Step1);
          }
        }),
      )
      .subscribe((result) => {
        const success = !!result;

        this.tracker.trackEvent({
          module: EventModule.Entry,
          journey: EventJourney.Create,
          action: EventAction.Completed,
          metadata: {
            entry: this.type,
            result: success ? EventMetadata.Success : EventMetadata.Failed,
          },
        });

        if (!result) return;

        if (
          this.navigatedFromContactPage &&
          this.isFromContactToIndividualFlow
        ) {
          this.createdIndividual = {
            id: result ?? '',
            name: this.form?.group.get('lastName')?.value,
          };
        }

        const existing = this.storage.get(
          this.outlookService.storagePartitionKey + this.newPartitionKey,
        );
        let entryArray: string[] = [];
        if (existing) {
          entryArray = JSON.parse(existing);
        }
        if (this.form) {
          entryArray.push(this.form.value.email1);
          this.storage.set(
            this.outlookService.storagePartitionKey + this.newPartitionKey,
            JSON.stringify(entryArray),
          );
        }

        this.notification().show('create', 'success');
        this.handleFormChangeNavigation();
      });
  }

  cancel(): void {
    if (this.onboardingStore.inProgress()) {
      this.skipOnboarding();
      return;
    }
    this.handleFormChangeNavigation();
  }

  private handleFormChangeNavigation(): void {
    if (this.navigatedFromContactPage) {
      this.navigatedFromContactPage = false;

      if (this.onboardingStore.inProgress()) {
        this.showOnboardingGuide();
      }
    }

    this.navigationService.navigateBack();
  }

  onEntryChange(event: string | null, prop: string, index: number): void {
    const array = prop.includes('email') ? this.emails : this.phones;

    const value = typeof event === 'string' ? event.trim() : '';

    if (prop.includes('Description')) {
      array[index].type = value;
    } else {
      array[index].value = value;
    }
  }

  onRemoveEntry(i: number, prop: string): void {
    if (prop.includes('email')) {
      this.emails.splice(i, 1);
      const array = structuredClone(this.emails); // update form reference
      this.emails = array;
      this.updateEmails();
    } else {
      const array = structuredClone(this.phones); // update form reference
      array.splice(i, 1);
      this.phones = array;
      this.updatePhones();
    }
  }

  onAddEntry(type: string): void {
    if (type.includes('email')) {
      this.emails.push({ type: '', value: '' });
    } else {
      this.phones.push({
        type: '',
        value: '',
      });
    }
  }

  getFieldLimit(prop: string): number {
    if (prop.includes('email')) return this.emailLimit;
    return this.phoneLimit;
  }

  private updateEmails(): void {
    this.form?.group.patchValue({
      email1: this.emails[0]?.value.trim() ?? '',
      email1Description: this.emails[0]?.type.trim() ?? '',
      email2: this.emails[1]?.value.trim() ?? '',
      email2Description: this.emails[1]?.type.trim() ?? '',
      email3: this.emails[2]?.value.trim() ?? '',
      email3Description: this.emails[2]?.type.trim() ?? '',
    });
    const company = this.form?.group.get('company');
    if (company) company.setErrors(null);
  }

  private updatePhones(): void {
    this.form?.patch({
      phone1: this.phones[0]?.value.trim() ?? '',
      phone1Description: this.phones[0]?.type.trim() ?? '',
      phone2: this.phones[1]?.value.trim() ?? '',
      phone2Description: this.phones[1]?.type.trim() ?? '',
      phone3: this.phones[2]?.value.trim() ?? '',
      phone3Description: this.phones[2]?.type.trim() ?? '',
      phone4: this.phones[3]?.value.trim() ?? '',
      phone4Description: this.phones[3]?.type.trim() ?? '',
    });
    const company = this.form?.group.get('company');
    if (company) company.setErrors(null);
  }

  handleCompanyValueChange(inputText: string): void {
    if (inputText == '') {
      this.companyNameSearch = '';
      this.companySelected = null;
      this.form?.patch({
        parentKey: null,
        company: '',
      });
      this.validateCompanyField();
      return;
    }

    let trimmedInput = inputText;
    if (inputText.length > 80) {
      trimmedInput = inputText.substring(0, 80);
      this.form?.patch({
        company: trimmedInput,
      });
      this.validateCompanyField();
      return;
    }

    this.companyNameSearch = trimmedInput;
    const contactData = this.filteredAbEntryList.find(
      (o) => o.name == trimmedInput,
    );

    if (contactData) {
      this.companySelected = contactData;
      this.form?.patch({
        parentKey: contactData.id,
        company: contactData.name,
      });
    } else if (
      trimmedInput != this.companySelected?.name &&
      this.companySelected?.id
    ) {
      this.form?.patch({
        parentKey: this.companySelected?.id,
        company: this.companySelected?.name,
      });
    }
    this.loadCompanyAddress();
    this.validateCompanyField();
  }

  handleCompanyBlur(): void {
    this.validateCompanyField();
  }

  openAutocompletePopup(): void {
    if (
      this.companyNameSearch.length < 3 ||
      this.companySelected?.name == this.companyNameSearch
    ) {
      this.autoComplete().toggle(false);
    } else {
      this.autoComplete().toggle();
    }
  }

  handleCompanyFilterChange(inputText: string): void {
    this.companyNameSearch = inputText.trim();
    if (inputText.length < 3) {
      this.autocompleteLoading = false;
      this.autoComplete().toggle(false);
      return;
    }

    if (inputText.length > 80) {
      this.companyNameSearch = inputText.trim().substring(0, 80);
      if (this.previousCompanyAutocompleteInput == this.companyNameSearch)
        return;
    }

    this.validateCompanyField();
    this.autocompleteLoading = true;
    const previous = this.companyNameSearch.toString();
    this.previousCompanyAutocompleteInput = previous;

    setTimeout(() => {
      if (this.companyNameSearch != previous) return;
      this.filteredAbEntryList = [];
      this.abEntryList = [];
      this.searchCompaniesAndIndividuals();
    }, 1000);
  }

  searchCompaniesAndIndividuals(): void {
    const requests = {
      companies: this.abEntrySearchService.search(
        this.companyNameSearch,
        true,
        15,
        Octopus.AbEntryType.Company,
      ),
      individuals: this.abEntrySearchService.search(
        this.companyNameSearch,
        true,
        15,
        Octopus.AbEntryType.Individual,
      ),
    };

    forkJoin(requests)
      .pipe(
        catchError(() => {
          this.notification().showMessage(
            'success',
            'outlook.abentry.companyNotification.loadError',
            true,
          );
          return of(null);
        }),
        finalize(() => (this.autocompleteLoading = false)),
      )
      .subscribe((result) => {
        if (!result) {
          return;
        }

        if (result.companies.length > 0 || result.individuals.length > 0) {
          const mappedCompanies = result.companies.map((entry) => ({
            id: entry.key ?? '',
            name: entry.companyName,
          }));

          const mappedIndividuals = result.individuals.map((entry) => ({
            id: entry.key ?? '',
            name: entry.name,
          }));

          this.filteredAbEntryList = [...mappedCompanies, ...mappedIndividuals];
        }

        this.abEntryList = this.filteredAbEntryList.filter((entry) =>
          entry.name
            .toLowerCase()
            .includes(this.companyNameSearch.toLowerCase()),
        );
      });
  }

  createCompany(): void {
    if (this.createCompanyLoading) return;

    const companyName = this.companyNameSearch.trim();
    if (!companyName) return;

    this.createCompanyLoading = true;

    this.abentryService
      .saveCompany(companyName)
      .pipe(
        catchError((error) => {
          this.trackCompanyCreateFailure(error);
          return of(null);
        }),
        finalize(() => {
          this.createCompanyLoading = false;
          this.loading = false;
        }),
      )
      .subscribe((result) => {
        if (!result) {
          this.notification().showMessage(
            'error',
            'outlook.abentry.companyNotification.saveError',
            true,
          );
          return;
        }

        this.companySelected = { id: result, name: companyName };
        this.form?.patch({
          parentKey: result,
          company: companyName,
        });
        this.clearCompanyAddress();

        this.autoComplete().toggle(false);
        this.validateCompanyField();

        this.notification().showMessage(
          'success',
          'outlook.abentry.companyNotification.save',
          true,
        );
      });
  }

  private trackCompanyCreateFailure(error: unknown): void {
    this.insightsService.trackEvent(
      { name: 'add-abentry-create-company-failed' },
      SeverityLevel.Error,
      { status: (error as { status?: number } | null)?.status ?? 'unknown' },
    );
  }

  showIndividualForm(): void {
    this.navigatedFromContactPage = true;
    this.isFromContactToIndividualFlow = true;
    this.addContact2Container()?.hide();

    if (this.form) {
      this.originalContactFormState?.group.patchValue(this.form.value);
      this.form.reset();
    }

    this.navigationService.backNavigationOverride.set({
      state: true,
      pathSegments: [this.addAbEntryPath, EntryType.Contact],
    });

    this.router.navigate([this.addAbEntryPath, EntryType.Individual]);
  }

  validateCompanyField(): void {
    const company = this.form?.group.get('company');
    if (!company) return;

    if (company && !this.companyValidatorSet) {
      company.setValidators(Validators.required);
    }

    if (this.companyNameSearch.length > 79) {
      company.setErrors({ maxlength: 79 });
    } else if (this.companyNameSearch == '') {
      company.setErrors({ required: true });
      this.clearCompanyAddress();
    } else if (
      this.companyNameSearch.length > 0 &&
      (this.form?.value.parentKey ?? '') == ''
    ) {
      company.setErrors({ exist: true });
    } else {
      company.setErrors(null);
    }
  }

  loadCompanyAddress(): void {
    let queryId = '';

    if (this.isFromContactToIndividualFlow && !this.navigatedFromContactPage) {
      this.isFromContactToIndividualFlow = false; //reset
      queryId = this.createdIndividual.id;
    } else {
      if (!this.companySelected?.id) return;
      if (this.type() !== EntryType.Contact) return;
      queryId = this.companySelected.id;
    }

    this.autocompleteLoading = true;

    this.abentryService
      .getAllDetailsByKey(queryId)
      .pipe(
        catchError(() => {
          this.notification().show('load', 'error');
          return of(null);
        }),
        finalize(() => (this.autocompleteLoading = false)),
      )
      .subscribe((data) => {
        if (!data) return;
        this.form?.patch({
          address1: data.address?.address1,
          address2: data.address?.address2,
          country: data.address?.country,
          city: data.address?.city,
          province: data.address?.provinceState,
          postalCode: data.address?.zipcode,
        });
      });
  }

  private clearCompanyAddress(): void {
    this.form?.patch({
      address1: '',
      address2: '',
      country: '',
      city: '',
      province: '',
      postalCode: '',
    });
  }

  private buildContactForm(
    firstName: string,
    lastName: string,
    isAddressDisabled: boolean,
    type: string,
    emails: { type: string; value: string }[],
    navigatedFromContactPage: boolean,
    companyNameSearch: string,
  ): FormGroupWrapper<OutlookAbEntryForm> {
    return FormWrapperBuilder.group<OutlookAbEntryForm>({
      key: null,
      firstName: [firstName, [Validators.maxLength(79)]],
      lastName: [
        navigatedFromContactPage ? companyNameSearch : lastName,
        [Validators.maxLength(79)],
      ],
      email1: [navigatedFromContactPage ? null : emails[0]?.value, []],
      email1Description: [null, [Validators.maxLength(15)]],
      email2: [null, [Validators.maxLength(255), Validators.email]],
      email2Description: [null, [Validators.maxLength(15)]],
      email3: [
        null,
        {
          validators: [Validators.maxLength(255), Validators.email],
          updateOn: 'blur',
        },
      ],
      email3Description: [null, [Validators.maxLength(15)]],
      phone1: [null, PHONE_VALIDATORS],
      phone1Description: [null, [Validators.maxLength(21)]],
      phone2: [null, PHONE_VALIDATORS],
      phone2Description: [null, [Validators.maxLength(21)]],
      phone3: [null, PHONE_VALIDATORS],
      phone3Description: [null, [Validators.maxLength(21)]],
      phone4: [null, PHONE_VALIDATORS],
      phone4Description: [null, [Validators.maxLength(21)]],
      website: [null, [Validators.maxLength(255)]],
      company: [null, [Validators.maxLength(79)]],
      position: [null, [Validators.maxLength(79)]],
      address1: [
        { value: null, disabled: isAddressDisabled },
        [Validators.maxLength(79)],
      ],
      address2: [
        { value: null, disabled: isAddressDisabled },
        [Validators.maxLength(79)],
      ],
      city: [
        { value: null, disabled: isAddressDisabled },
        [Validators.maxLength(79)],
      ],
      province: [
        { value: null, disabled: isAddressDisabled },
        [Validators.maxLength(79)],
      ],
      postalCode: [
        { value: null, disabled: isAddressDisabled },
        [Validators.maxLength(79)],
      ],
      country: [
        { value: null, disabled: isAddressDisabled },
        [Validators.maxLength(79)],
      ],
      addressKey: [null],
      parentKey: [
        null,
        type === EntryType.Individual ? [] : [Validators.required],
      ],
      type: type,
    });
  }

  onFormFocusIn(): void {
    if (
      this.onboardingStore.inProgress() &&
      this.onboardingStore.currentGuide() === StepGuideId.AddContact2
    ) {
      this.addContact2Container()?.hide();
    }
  }

  onFormFocusOut(event: FocusEvent): void {
    if (
      this.onboardingStore.inProgress() &&
      this.onboardingStore.currentGuide() === StepGuideId.AddContact2
    ) {
      const container = this.pageContainer().nativeElement;
      if (!container.contains(event.relatedTarget as Node)) {
        this.showAddContact2Popover();
      }
    }
  }

  // Onboarding
  showOnboardingGuide(): void {
    this.onboardingStore.setCurrentGuide(StepGuideId.AddContact2);
    setTimeout(() => {
      this.showAddContact2Popover();
    }, ONBOARDING_STEP_POPOVER_OPEN_DELAY);
  }

  private showAddContact2Popover(): void {
    const anchor = this.addContact2Anchor();
    if (anchor) {
      this.addContact2Container()?.show(anchor);
    }
  }

  skipOnboarding(): void {
    this.nextOnboardingStep(OnboardingStepNumber.Step1);
    this.navigationService.navigateBack();
  }

  nextOnboardingStep(currentStep: OnboardingStepNumber): void {
    if (currentStep < OnboardingStepNumber.Step4) {
      this.onboardingStore.setCurrentStep(currentStep + 1);
    }
  }

  private async initializeAiPrefill(): Promise<void> {
    if (
      this.outlookStore.isSentFolder() ||
      this.outlookService.isCalendarItem
    ) {
      return;
    }

    if (
      !this.form ||
      !this.signatureAi ||
      this.type() === EntryType.Company ||
      this.navigatedFromContactPage ||
      this.isFromContactToIndividualFlow
    ) {
      return;
    }

    try {
      const body = await firstValueFrom(
        this.outlookService.getMessageBodyText(),
      );

      if (!body || body.length < AI_EXTRACTION_MIN_BODY_LENGTH) {
        return;
      }

      const newContactEmail = this.form.value.email1;
      this.processSignatureData(newContactEmail, body);
    } catch (err) {
      console.error('[AddAbEntry] Prefill failed:', err);
      this.aiLoading = false;
    }
  }

  private processSignatureData(newContactEmail: string, body: string): void {
    const firstName = this.form?.value.firstName?.trim() ?? '';
    const lastName = this.form?.value.lastName?.trim() ?? '';
    const fullName = `${firstName} ${lastName}`.trim();

    this.aiLoading = true;

    this.signatureAi
      .extract(newContactEmail, fullName, body)
      .pipe(
        catchError((error) => {
          this.insightsService.trackEvent(
            { name: 'add-abentry-ai-extraction-failed' },
            SeverityLevel.Error,
            {
              error: error.message,
              eventId: OutlookInsightsEvents.AiAutoPopulate,
            },
          );

          return of(null);
        }),
        finalize(() => (this.aiLoading = false)),
      )
      .subscribe((aiResult) => {
        if (!aiResult || !this.form) {
          return;
        }

        const patch: Partial<OutlookAbEntryForm> = {};

        this.patchAiPrefillNameFields(aiResult, patch);

        if (
          this.type() === EntryType.Individual &&
          this.canPatchField('company') &&
          aiResult.company
        ) {
          patch.company = aiResult.company;
        }

        if (this.canPatchField('position') && aiResult.position) {
          patch.position = aiResult.position;
        }

        if (this.canPatchField('website') && aiResult.website) {
          patch.website = aiResult.website;
        }

        if (this.canPatchField('phone1') && aiResult.phones?.[0]) {
          patch.phone1 = aiResult.phones[0];
          this.updatePhoneAtIndex(0, aiResult.phones[0]);
        }

        if (this.canPatchField('phone2') && aiResult.phones?.[1]) {
          patch.phone2 = aiResult.phones[1];
          this.updatePhoneAtIndex(1, aiResult.phones[1]);
        }

        if (
          this.type() === EntryType.Individual &&
          aiResult.address &&
          this.canPatchField('address1')
        ) {
          patch.address1 = aiResult.address;
        }

        if (Object.keys(patch).length > 0) {
          this.form.patch(patch);
        }
      });
  }

  private patchAiPrefillNameFields(
    aiResult: EmailSignatureFields,
    patch: Partial<OutlookAbEntryForm>,
  ): void {
    // Only populate name fields if both are empty
    const currentFirstName = this.form?.value.firstName?.trim();
    const currentLastName = this.form?.value.lastName?.trim();

    if (!currentFirstName && !currentLastName) {
      if (aiResult.firstName) {
        patch.firstName = aiResult.firstName;
      }

      if (aiResult.lastName) {
        patch.lastName = aiResult.lastName;
      }
    }
  }

  private canPatchField(controlName: keyof OutlookAbEntryForm): boolean {
    const control = this.form?.group.get(controlName);
    return !!control && !control.disabled && !control.value;
  }

  private updatePhoneAtIndex(index: number, value: string): void {
    while (this.phones.length <= index) {
      this.phones.push({ type: '', value: '' });
    }
    this.phones[index].value = value;
  }
}
